import type { AuthUser } from "~/features/auth/auth";
import type { HttpPort } from "~/lib/http";

export interface StudentClass {
  id: string;
  name: string;
  school_name?: string | null;
  teacher_school_name?: string | null;
  teacher_name?: string;
  join_code?: string;
  monster_name?: string | null;
  body_color?: string | null;
  level?: number | null;
  happiness?: number | null;
}

export interface StudentWorkspace {
  user: AuthUser;
  classes: StudentClass[];
  selectedClass?: StudentClass;
}

export type HelpCategory = "feeling_bad" | "bullying" | "talk" | "other";

export function studentClassLabel(value?: StudentClass) {
  if (!value) return "";
  const school = value.teacher_school_name || value.school_name;
  return school ? `${value.name} · ${school}` : value.name;
}

export function createStudentModule(http: HttpPort, token: string) {
  return {
    async load(preferredClassId?: string): Promise<StudentWorkspace> {
      const [account, classResult] = await Promise.all([
        http.request<{ user: AuthUser }>({ path: "/auth/me", token }),
        http.request<{ classes?: StudentClass[] }>({ path: "/students/my-classes", token })
      ]);
      const classes = classResult.classes ?? [];
      return {
        user: account.user,
        classes,
        selectedClass: classes.find((value) => value.id === preferredClassId) ?? classes[0]
      };
    },
    async join(joinCode: string): Promise<StudentClass> {
      const normalized = joinCode.trim().toUpperCase();
      if (!normalized) throw new Error("Code eingeben");
      const result = await http.request<{ class: StudentClass }>({ path: "/classes/join", method: "POST", token, body: { joinCode: normalized } });
      return result.class;
    },
    async requestHelp(classId: string | undefined, category: HelpCategory | undefined, message: string): Promise<void> {
      if (!category) throw new Error("Bitte wähle eine Kategorie aus");
      if (!classId) throw new Error("Keine Klasse ausgewählt");
      const normalized = message.trim();
      await http.request({ path: "/help", method: "POST", token, body: { classId, category, ...(normalized ? { message: normalized } : {}) } });
    }
  };
}

export type StudentModule = ReturnType<typeof createStudentModule>;
